const timeScale = [
  {
    range: [-Infinity, 90],
    scale: 1,
    label: "second",
    abbrev: "sec",
    decimals: 0,
  },
  {
    range: [90, 5400],
    scale: 60,
    label: "minute",
    abbrev: "min",
    decimals: 1,
  },
  {
    range: [5400, 172800],
    scale: 3600,
    label: "hour",
    abbrev: "hr",
    decimals: 1,
  },
  {
    range: [172800, 1209600],
    scale: 86400,
    label: "day",
    abbrev: "d",
    decimals: 1,
  },
  {
    range: [1209600, 4838400],
    scale: 604800,
    label: "week",
    abbrev: "wk",
    decimals: 1,
  },
  {
    range: [4838400, Infinity],
    scale: 2592000,
    label: "month",
    abbrev: "mo",
    decimals: 1,
  },
];

const findScaleForUnit = (unit) =>
  timeScale.find((s) => s.label === unit || s.label + "s" === unit);

export const formatTime = (
  secondsValue,
  unit = null,
  { minUnit = null, maxUnit = null } = {},
) => {
  const seconds = secondsValue || 0; // use 0 if not specified

  // If unit is provided, scale seconds to the provided unit
  if (unit) {
    const scaleItem = findScaleForUnit(unit);
    if (scaleItem) {
      const value = (seconds / scaleItem.scale).toFixed(scaleItem.decimals);
      const outputUnit = `${scaleItem.label}${value == 1 ? "" : "s"}`;
      return {
        value: parseFloat(value),
        unit: outputUnit,
        abbrev: scaleItem.abbrev,
      };
    }
  }

  // If unit is not provided, scale dynamically
  let scale = timeScale.find(
    (s) => seconds >= s.range[0] && seconds < s.range[1],
  );

  // optionally cap units on either end of the scale
  if (minUnit) {
    const minScaleItem = findScaleForUnit(minUnit);
    if (minScaleItem && scale.scale < minScaleItem.scale) {
      scale = minScaleItem;
    }
  }
  if (maxUnit) {
    const maxScaleItem = findScaleForUnit(maxUnit);
    if (maxScaleItem && scale.scale > maxScaleItem.scale) {
      scale = maxScaleItem;
    }
  }

  const value = (seconds / scale.scale).toFixed(scale.decimals);
  const outputUnit = `${scale.label}${value == 1 ? "" : "s"}`;
  return {
    value: parseFloat(value),
    unit: outputUnit,
    abbrev: scale.abbrev,
  };
};

// return time optionally with a lesser value/unit for the remainder e.g. "1 hour" + "30 minutes" instead of "1.5 hours"
// supplying an equivalent to formatTime just in case we need to use the units somewhere
export const formatTimeMultiUnit = (
  secondsValue,
  unit = null,
  { minUnit = null, maxUnit = null } = {},
) => {
  const seconds = secondsValue || 0; // use 0 if not specified

  let scale;

  // If unit is provided, scale seconds to the provided unit
  if (unit) scale = findScaleForUnit(unit);

  if (!scale) {
    // If unit is not provided, scale dynamically
    scale = timeScale.find(
      (s) => seconds >= s.range[0] && seconds < s.range[1],
    );

    // optionally cap units on either end of the scale
    if (minUnit) {
      const minScaleItem = findScaleForUnit(minUnit);
      if (minScaleItem && scale.scale < minScaleItem.scale) {
        scale = minScaleItem;
      }
    }
    if (maxUnit) {
      const maxScaleItem = findScaleForUnit(maxUnit);
      if (maxScaleItem && scale.scale > maxScaleItem.scale) {
        scale = maxScaleItem;
      }
    }
  }

  // get the usual scaled value rounded down
  const primaryValue = Math.floor(seconds / scale.scale);
  const remainder = seconds - primaryValue * scale.scale;

  // find the next smallest unit and use it with the remaineder
  const secondaryScale =
    scale === "seconds" ? null : timeScale[timeScale.indexOf(scale) - 1];

  const formatValue = (value, scale) => {
    const outputUnit = `${scale.label}${value == 1 ? "" : "s"}`;
    return {
      value,
      unit: outputUnit,
      abbrev: scale.abbrev,
    };
  };

  return {
    primary: formatValue(primaryValue, scale),
    secondary:
      secondaryScale &&
      formatValue(Math.round(remainder / secondaryScale.scale), secondaryScale),
  };
};

// e.g. 5400 -> 1hr 30min (abbreviated) or 1 hour 30 minutes (not abbreviated)
export const timeToHumanizedString = (
  secondsValue,
  { abbreviated = true, ...opts } = {},
) => {
  const { primary, secondary } = formatTimeMultiUnit(secondsValue, null, opts);

  return [primary, secondary]
    .filter((v) => v?.value)
    .map((v) => `${v.value}${abbreviated ? v.abbrev : ` ${v.unit}`}`)
    .join(" ");
};
